import { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import confetti from "canvas-confetti";
import {
  Gift, Sparkles, ArrowLeft, Save, LogIn, UserPlus, X, Receipt,
  CheckCircle2, Copy,
} from "lucide-react";
import { api, formatApiError } from "@/lib/apiClient";
import { memberApi, formatMemberError } from "@/lib/memberClient";
import { useMember } from "@/contexts/MemberContext";
import { BRAND } from "@/constants/brand";
import PopReveal from "@/components/PopReveal";

const REWARD_ICON = {
  coins: "🪙",
  popsicle: "🍭",
  popcorn: "🍿",
  discount: "🎟",
  bogo: "🎁",
  mystery: "🎲",
  festival: "🎉",
  custom: "✨",
  nothing: "🥲",
};

export default function PayScratchCard() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const paymentId = params.get("p");
  const { member } = useMember();
  const [card, setCard] = useState(null);
  const [revealed, setRevealed] = useState(false);
  const [reward, setReward] = useState(null);
  const [saved, setSaved] = useState(false);
  const [saveData, setSaveData] = useState(null);
  const [authOpen, setAuthOpen] = useState(false);

  useEffect(() => {
    if (!token) return;
    api.get(`/pay-scratch/card/${token}`).then(({ data }) => {
      setCard(data);
      if (data.revealed) {
        setReward(data.reward);
        setRevealed(true);
      }
      if (data.saved) setSaved(true);
    }).catch((e) => toast.error(formatApiError(e)));
  }, [token]);

  // Fetch the reward upfront so the Pop Reveal animation can show it emerging
  // through the cracks. (Security: reward is already server-picked & bound to
  // this card at payment-verify time; showing it here doesn't change anything.)
  useEffect(() => {
    if (!token || !card || reward) return;
    api.post(`/pay-scratch/card/${token}/reveal`).then(({ data }) => {
      setReward(data.reward);
    }).catch(() => { /* silent */ });
  }, [token, card, reward]);

  const onRevealed = () => {
    if (revealed) return;
    setRevealed(true);
    if (reward?.is_win) {
      const shoot = () => confetti({ particleCount: 130, spread: 90, origin: { y: 0.4 }, colors: ["#FFC93C", "#FFE07A", "#E5A100", "#FF4F5A", "#fff"] });
      shoot();
      setTimeout(shoot, 250);
      setTimeout(shoot, 550);
    }
  };

  const saveReward = async () => {
    if (!member) { setAuthOpen(true); return; }
    try {
      const { data } = await memberApi.post(`/pay-scratch/card/${token}/save`);
      setSaved(true);
      setSaveData(data);
      toast.success(data.already_saved ? "Reward is already saved" : "Reward saved to your Panda Club");
    } catch (e) {
      toast.error(formatMemberError(e));
    }
  };

  // Auto-save after login (if user just authenticated)
  useEffect(() => {
    if (authOpen && member && revealed && reward?.is_win && !saved) {
      setAuthOpen(false);
      saveReward();
    }
    // eslint-disable-next-line
  }, [member, authOpen]);

  if (!card) return <div className="min-h-screen bg-[#0a0714] text-brand-cream grid place-items-center">Loading your scratch card…</div>;

  return (
    <div
      data-testid="scratch-card-page"
      className="min-h-screen text-brand-cream relative overflow-hidden"
      style={{ background: "radial-gradient(ellipse at 30% 10%, #1e0d3e 0%, #0e0a24 40%, #0a0714 80%, #050308 100%)" }}
    >
      {/* Ambient bg */}
      <div className="absolute inset-0 pointer-events-none">
        <motion.div className="absolute -top-40 -left-32 w-[520px] h-[520px] rounded-full blur-3xl" style={{ background: "radial-gradient(circle, rgba(255,201,60,0.24), transparent 60%)" }} animate={{ x: [0, 30, 0], y: [0, 20, 0] }} transition={{ duration: 14, repeat: Infinity, ease: "easeInOut" }} />
        <motion.div className="absolute bottom-0 -right-32 w-[440px] h-[440px] rounded-full blur-3xl" style={{ background: "radial-gradient(circle, rgba(139,92,246,0.24), transparent 60%)" }} animate={{ x: [0, -30, 0], y: [0, -20, 0] }} transition={{ duration: 16, repeat: Infinity, ease: "easeInOut" }} />
        {Array.from({ length: 30 }).map((_, i) => (
          <span key={i} className="absolute rounded-full bg-white" style={{ left: `${(i * 41) % 100}%`, top: `${(i * 67) % 100}%`, width: 2, height: 2, opacity: 0.15 + (i % 3) * 0.1 }} />
        ))}
      </div>

      <header className="relative z-10 flex items-center justify-between px-5 py-5 max-w-lg mx-auto">
        <Link to="/" className="flex items-center gap-2">
          <ArrowLeft size={16} className="text-brand-cream/70" />
          <img src={BRAND.logoUrl} alt="Pop Panda" className="h-8 w-8 bg-brand-cream rounded-full p-1" />
          <div className="font-display text-lg">Pop Panda</div>
        </Link>
        {paymentId && (
          <Link to={`/scratch/receipt/${paymentId}`} className="inline-flex items-center gap-1.5 text-xs text-brand-cream/70 hover:text-brand-cream" data-testid="link-receipt">
            <Receipt size={13} /> Receipt
          </Link>
        )}
      </header>

      <main className="relative z-10 max-w-lg mx-auto px-5 pb-16">
        <div className="text-center">
          <div className="text-[10px] uppercase tracking-[0.32em] text-brand-yellow inline-flex items-center gap-1.5"><Sparkles size={11} /> Pop Reveal&trade;</div>
          <h1 className="font-display text-3xl mt-2">{revealed ? (reward?.is_win ? "You won!" : "So close!") : "Your Pop Surprise awaits"}</h1>
          <p className="text-brand-cream/60 text-sm mt-1">{revealed ? "Your surprise is here." : "Slide the popsicle stick down to break the frost."}</p>
        </div>

        <div className="mt-6 flex justify-center">
          <PopReveal
            onRevealed={onRevealed}
            initiallyRevealed={revealed}
            reward={reward || { type: "mystery", name: "Your Pop Surprise" }}
          />
        </div>

        {revealed && reward && (
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
            className="mt-6 rounded-3xl bg-white/[0.06] border border-white/15 backdrop-blur-2xl p-6 text-center"
            data-testid="reward-detail"
          >
            <div className="text-4xl">{REWARD_ICON[reward.type] || "✨"}</div>
            <div className="font-display text-2xl mt-2">{reward.name}</div>
            {reward.description && <div className="text-sm text-brand-cream/70 mt-1">{reward.description}</div>}
            {reward.is_win && reward.expiry_days > 0 && (
              <div className="mt-3 text-[11px] uppercase tracking-widest text-brand-cream/50">
                Valid for {reward.expiry_days} days after saving
              </div>
            )}
            {saveData?.reward_code && (
              <div className="mt-4 rounded-2xl bg-brand-yellow/10 border border-brand-yellow/40 p-4">
                <div className="text-[10px] uppercase tracking-widest text-brand-yellow">Your redemption code</div>
                <div className="mt-1 flex items-center justify-between gap-3">
                  <div className="font-mono text-2xl text-brand-yellow tracking-widest" data-testid="reward-code">{saveData.reward_code}</div>
                  <button onClick={() => { navigator.clipboard.writeText(saveData.reward_code); toast.success("Copied"); }} className="rounded-full bg-white/10 hover:bg-white/20 border border-white/20 p-2 transition" data-testid="copy-reward-code">
                    <Copy size={13} />
                  </button>
                </div>
              </div>
            )}
            {reward.is_win && !saved && (
              <button
                onClick={saveReward}
                data-testid="save-reward-btn"
                className="mt-5 w-full rounded-full px-6 py-3.5 text-sm font-semibold text-[#1a0f2f] transition-all"
                style={{ background: "linear-gradient(135deg, #FFE07A 0%, #FFC93C 50%, #E5A100 100%)", boxShadow: "0 12px 32px rgba(255,201,60,0.4)" }}
              >
                <Save size={14} className="inline mr-1" /> Save Reward to Panda Club
              </button>
            )}
            {saved && (
              <div className="mt-5 rounded-2xl border border-emerald-400/40 bg-emerald-500/10 p-4 flex items-center gap-3 text-left" data-testid="saved-banner">
                <CheckCircle2 size={22} className="text-emerald-400 shrink-0" />
                <div>
                  <div className="font-medium">Saved to your Panda Club!</div>
                  <div className="text-xs text-brand-cream/60">Find it in your Rewards section.</div>
                </div>
              </div>
            )}
            {revealed && !reward.is_win && (
              <div className="mt-5 text-xs text-brand-cream/60">
                Better luck next time — every purchase is another chance!
              </div>
            )}
            <div className="mt-5 flex justify-center gap-2 flex-wrap">
              {paymentId && (
                <Link to={`/scratch/receipt/${paymentId}`} className="text-xs rounded-full bg-white/10 border border-white/20 px-3 py-1.5 hover:bg-white/20 inline-flex items-center gap-1.5" data-testid="view-receipt-btn"><Receipt size={12} /> View receipt</Link>
              )}
              <Link to="/" className="text-xs rounded-full bg-white/10 border border-white/20 px-3 py-1.5 hover:bg-white/20">Back home</Link>
            </div>
          </motion.div>
        )}
      </main>

      {/* Auth prompt */}
      <AnimatePresence>
        {authOpen && (
          <AuthGate onClose={() => setAuthOpen(false)} onDone={() => { /* MemberContext will trigger effect */ }} />
        )}
      </AnimatePresence>
    </div>
  );
}

/* ============= Auth Gate (inline) ============= */
function AuthGate({ onClose }) {
  const [mode, setMode] = useState("signin"); // signin | signup
  const { login, signup } = useMember();
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "" });

  const submit = async () => {
    setBusy(true);
    let res;
    if (mode === "signin") res = await login(form.email, form.password);
    else res = await signup(form);
    setBusy(false);
    if (!res.ok) return toast.error(res.error);
    toast.success(mode === "signin" ? "Welcome back!" : "Welcome to Panda Club!");
    onClose?.();
  };

  return (
    <motion.div
      className="fixed inset-0 z-50 grid place-items-center bg-black/80 backdrop-blur-md px-4"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      data-testid="auth-gate"
    >
      <motion.div
        initial={{ scale: 0.85, opacity: 0, y: 30 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        transition={{ type: "spring", stiffness: 160, damping: 18 }}
        className="relative max-w-sm w-full rounded-3xl p-6 text-brand-cream"
        style={{ background: "linear-gradient(160deg, #1e0d3e 0%, #0e0a24 100%)", border: "1px solid rgba(255,201,60,0.35)" }}
      >
        <button onClick={onClose} className="absolute top-3 right-3 text-brand-cream/60 hover:text-brand-cream" data-testid="auth-close"><X size={18} /></button>
        <div className="text-center">
          <div className="mx-auto w-14 h-14 rounded-full grid place-items-center" style={{ background: "radial-gradient(circle at 30% 30%, #fff1b3 0%, #FFC93C 50%, #E5A100 100%)" }}>
            <Gift size={26} className="text-[#3a2200]" />
          </div>
          <div className="mt-3 text-[10px] uppercase tracking-[0.28em] text-brand-yellow">Save your reward</div>
          <div className="font-display text-2xl mt-1">{mode === "signin" ? "Sign in to Panda Club" : "Join Panda Club — Free"}</div>
          <div className="text-xs text-brand-cream/60 mt-1">Your reward will be linked automatically.</div>
        </div>

        <div className="mt-4 flex gap-1 rounded-full bg-white/8 p-1">
          <button onClick={() => setMode("signin")} data-testid="auth-tab-signin" className={`flex-1 rounded-full py-1.5 text-xs uppercase tracking-widest transition ${mode === "signin" ? "bg-brand-yellow text-[#1a0f2f] font-semibold" : "text-brand-cream/70"}`}>Sign in</button>
          <button onClick={() => setMode("signup")} data-testid="auth-tab-signup" className={`flex-1 rounded-full py-1.5 text-xs uppercase tracking-widest transition ${mode === "signup" ? "bg-brand-yellow text-[#1a0f2f] font-semibold" : "text-brand-cream/70"}`}>Register</button>
        </div>

        <div className="mt-4 space-y-3">
          {mode === "signup" && (
            <>
              <input placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full rounded-xl bg-black/40 border border-white/15 px-3 py-2.5 text-sm outline-none focus:border-brand-yellow" data-testid="auth-name" />
              <input placeholder="Phone (10 digits)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="w-full rounded-xl bg-black/40 border border-white/15 px-3 py-2.5 text-sm outline-none focus:border-brand-yellow" data-testid="auth-phone" />
            </>
          )}
          <input placeholder="Email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full rounded-xl bg-black/40 border border-white/15 px-3 py-2.5 text-sm outline-none focus:border-brand-yellow" data-testid="auth-email" />
          <input placeholder="Password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="w-full rounded-xl bg-black/40 border border-white/15 px-3 py-2.5 text-sm outline-none focus:border-brand-yellow" data-testid="auth-password" />
        </div>

        <button
          onClick={submit}
          disabled={busy}
          data-testid="auth-submit"
          className="mt-5 w-full rounded-full px-6 py-3 text-sm font-semibold text-[#1a0f2f] disabled:opacity-60 transition-all inline-flex items-center justify-center gap-2"
          style={{ background: "linear-gradient(135deg, #FFE07A 0%, #FFC93C 50%, #E5A100 100%)", boxShadow: "0 10px 28px rgba(255,201,60,0.4)" }}
        >
          {mode === "signin" ? <><LogIn size={14} /> Sign in & save</> : <><UserPlus size={14} /> Join & save</>}
        </button>
      </motion.div>
    </motion.div>
  );
}
